/**
 * GroupedExtractionTable Component
 *
 * Table wrapper that displays documents grouped by category.
 * Uses ExtractionGroupSection for collapsible groups.
 */

import { useState, ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { ExtractionGroupSection } from './ExtractionGroupSection';
import type { ExtractionDocumentGroup } from '@/types/extraction-grouping';
import type {
  DocumentWithExtraction,
  EncompassAttachmentWithExtraction
} from '@/hooks/useExtractionData';

// Column configurations for different sources
const LENDWISE_COLUMNS = [
  { key: 'document', label: 'Document', className: 'w-[250px]' },
  { key: 'type', label: 'Type', className: '' },
  { key: 'confidence', label: 'Conf.', className: 'w-[80px]' },
  { key: 'status', label: 'Status', className: 'w-[120px]' },
  { key: 'gpu', label: 'GPU', className: 'w-[100px]' },
  { key: 'time', label: 'Time', className: 'w-[80px] text-right' },
  { key: 'pages', label: 'Pages', className: 'w-[60px] text-right' },
  { key: 'chars', label: 'Chars', className: 'w-[80px] text-right' },
  { key: 'action', label: 'Action', className: 'w-[120px]' }
];

const ENCOMPASS_COLUMNS = [
  { key: 'document', label: 'Document', className: 'w-[45%]' },
  { key: 'status', label: 'Status', className: 'w-[35%]' },
  { key: 'time', label: 'Time', className: 'w-[10%] text-right' },
  { key: 'chars', label: 'Chars', className: 'w-[10%] text-right' }
];

interface GroupedExtractionTableProps<T> {
  /** Grouped documents/attachments */
  groups: ExtractionDocumentGroup<T>[];
  /** Source type to determine column layout */
  source: 'lendwise' | 'encompass';
  /** Render function for a single document row */
  renderRow: (item: T) => ReactNode;
}

export function GroupedExtractionTable<T extends DocumentWithExtraction | EncompassAttachmentWithExtraction>({
  groups,
  source,
  renderRow
}: GroupedExtractionTableProps<T>) {
  // Track expanded groups - all collapsed by default
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set()
  );

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const columns = source === 'lendwise' ? LENDWISE_COLUMNS : ENCOMPASS_COLUMNS;
  const colSpan = columns.length;

  return (
    <Table className={source === 'encompass' ? 'table-fixed' : undefined}>
      <TableHeader>
        <TableRow>
          {columns.map(col => (
            <TableHead key={col.key} className={col.className}>
              {col.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.map(group => (
          <ExtractionGroupSection
            key={group.groupName}
            group={group}
            isExpanded={expandedGroups.has(group.groupName)}
            onToggle={() => toggleGroup(group.groupName)}
            colSpan={colSpan}
            renderContent={() => (
              <>
                {group.documents.map((item, index) => (
                  <span key={(item as { id: string }).id || index}>
                    {renderRow(item)}
                  </span>
                ))}
              </>
            )}
          />
        ))}
      </TableBody>
    </Table>
  );
}

// Re-export for convenience
export type { ExtractionDocumentGroup };
